import React, {useEffect, useState} from "react"
import {
    Box,
    Paper,
    Typography,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Tooltip,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import {Stack} from "@mui/system"
import {useTranslation} from "react-i18next"
import i18n from "../i18n/i18n"
import {useNavigate} from "react-router-dom"
import axiosInstance from "../config/axios-config"
import {useBasket} from "../providers/basket-context"
import ConfirmationDialog from "../dialog/confirmation-dialog";
import GlowingMessage from "../errors/glowing-message";
import {cartTotalsLabel} from "./common/cart-pricing";
import BasketProductRow from "./basket-product-row";
import ProductDetailsDialog from "../dialog/product-details-dialog";

export const BasketContainer = ({onClose}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [basketItems, setBasketItems] = useState([])
    const {clearBasket, updateCountOfBasketItem, deleteBasketItem} = useBasket()
    const [productCounts, setProductCounts] = useState({})
    const [dialogOpen, setDialogOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);

    const handleConfirm = () => {
        handleClearBasket().then(r => console.log(r))
        setDialogOpen(false);
    };

    const handleCancel = () => {
        setDialogOpen(false);
    };

    const fetchProducts = async () => {
        setLoading(true)
        try {
            const response = await axiosInstance.get("/api/v1/carts/cart-table", {
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })
            setBasketItems(response.data)

            // Initialize productCounts with current quantities
            const initialCounts = {}
            response.data.forEach((distributor) => {
                distributor.items.forEach((item) => {
                    initialCounts[item.cartId] = item.quantity
                })
            })
            setProductCounts(initialCounts)
        } catch (error) {
            setMessage(error.response?.data?.message || "Failed to fetch products in the cart.")
            console.error("Error fetching products:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchProducts().then((r) => console.log(r))
    }, [])

    // Handler for quantity changes - updated to work with QuantitySelector
    const handleInputChange = async (distributorIndex, itemIndex, newValue) => {
        try {
            // Create a unique key for this item
            const itemKey = basketItems[distributorIndex].items[itemIndex].cartId

            // Get the previous value to check if it changed
            const prevValue = productCounts[itemKey] || 0
            const numValue = Number(newValue)

            // Update local state immediately for responsive UI
            setProductCounts((prevCounts) => ({
                ...prevCounts,
                [itemKey]: numValue,
            }))

            // Only call backend if value actually changed
            if (numValue !== prevValue && numValue > 0) {
                const item = basketItems[distributorIndex].items[itemIndex]

                if (item && item.cartId) {
                    await updateCountOfBasketItem(item.cartId, numValue)
                    // No need to fetch products here - we'll update UI optimistically
                    // and refresh on blur or add to basket
                }
            }
        } catch (error) {
            console.error("Error updating basket:", error)
            // Revert to previous state on error
            await fetchProducts()
        }
    }

    const handleAddToBasketTrigger = async (distributorIndex, itemIndex) => {
        try {
            const itemKey = basketItems[distributorIndex].items[itemIndex].cartId
            const item = basketItems[distributorIndex].items[itemIndex]
            const quantity = productCounts[itemKey] || 0

            if (quantity > 0 && quantity !== item.quantity) {
                await updateCountOfBasketItem(item.cartId, quantity)
                await fetchProducts() // Refresh data after update
            }
        } catch (error) {
            console.error("Error updating basket:", error)
        }
    }

    const handleRemoveItem = async (cartId) => {
        try {
            console.log(cartId)
            await deleteBasketItem(cartId)
            await fetchProducts()
        } catch (error) {
            console.error("Error removing item:", error)
        }
    }

    // Handler for clearing the entire basket
    const handleClearBasket = async () => {
        try {
            const cartIds = []
            basketItems.forEach((distributor) => {
                distributor.items.forEach((item) => {
                    if (item.cartId) {
                        cartIds.push(item.cartId)
                    }
                })
            })
            if (cartIds.length === 0) {
                return
            }
            console.log(cartIds)
            await clearBasket(cartIds)
            setBasketItems([])
        } catch (error) {
            console.error("Error clearing basket:", error)
        }
    }

    const calculateDistributorItemCount = items => items.reduce((count, item) =>
        count + (productCounts[item.cartId] ?? item.quantity), 0)

    // Each currency retains its own total; currencies are never added together.
    const calculateGrandTotal = () => cartTotalsLabel(basketItems.flatMap(distributor => distributor.items), productCounts)

    // Calculate grand total item count across all distributors
    const calculateGrandItemCount = () => {
        return basketItems.reduce((count, distributor) => {
            return count + calculateDistributorItemCount(distributor.items)
        }, 0)
    }

    // The badge counts cart lines; quantities are a separate unit count in group/footer summaries.
    const countLabel = items => t("BasketCounts", {lines: items.length, units: calculateDistributorItemCount(items)})

    const handleCheckout = () => {
        navigate("/basket")
    }

    return (
        <>
            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: 0,
                    overflow: "hidden", // Prevent content overflow
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 24px",
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "24px",
                            lineHeight: "133%",
                        }}
                    >
                        {t("Basket")}
                    </Typography>
                    <Tooltip title={t("Close")} arrow>
                        <IconButton onClick={onClose} aria-label={t("Close")} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </Box>
                <Divider/>

                <Box sx={{flexGrow: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "0 16px"}}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                            <CircularProgress/>
                        </Box>
                    ) : basketItems.length === 0 ? (
                        <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "100%"}}>
                            <Typography color="text.secondary">{t("EmptyBasket")}</Typography>
                        </Box>
                    ) : (
                        basketItems.map((distributor, distributorIndex) => (
                            <Box key={distributor.distributorId} sx={{mb: 3}}>
                                <Box sx={{display: "flex", justifyContent: "space-between", mt: 2, mb: 1}}>
                                    <Typography variant="subtitle1" fontWeight="medium" sx={{overflowWrap: "anywhere", minWidth: 0}}>
                                        {t("Distributor")}: {distributor.distributorName}
                                    </Typography>
                                </Box>

                                {/* Items table */}
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{
                                        mb: 2,
                                        overflowX: "hidden", // Prevent horizontal scrolling
                                    }}
                                >
                                    <Table size="small" sx={{tableLayout: "fixed", display: {xs: "block", sm: "table"}}}>
                                        <TableHead sx={{display: {xs: "none", sm: "table-header-group"}}}>
                                            <TableRow>
                                                <TableCell padding="none" aria-label={t("Actions")} sx={{width: 44, px: 0}}/>
                                                <TableCell sx={{width: "auto", pl: 1}}>{t("Product")}</TableCell>
                                                <TableCell sx={{width: 124, px: 0.5}}>
                                                    {t("Price")}
                                                </TableCell>
                                                <TableCell align="center" sx={{width: 104, px: 0}}>
                                                    {t("Quantity")}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody sx={{display: {xs: "block", sm: "table-row-group"}}}>
                                            {distributor.items.map((item, itemIndex) => <BasketProductRow key={item.cartId} item={item}
                                                quantity={productCounts[item.cartId] ?? item.quantity ?? 1}
                                                onChange={value => handleInputChange(distributorIndex, itemIndex, value)}
                                                onBlur={() => handleAddToBasketTrigger(distributorIndex, itemIndex)}
                                                onRemove={handleRemoveItem} onDetails={setSelectedProduct}/>)}
                                            {/* Distributor subtotal */}
                                            <TableRow sx={{display: {xs: "block", sm: "table-row"}}}>
                                                <TableCell colSpan={4} align="right" sx={{display: {xs: "block", sm: "table-cell"}}}>
                                                    <Typography variant="subtitle2" sx={{overflowWrap: "anywhere"}}>
                                                        {t("Subtotal")}: {cartTotalsLabel(distributor.items, productCounts)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">{countLabel(distributor.items)}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ))
                    )}
                </Box>

                <Box sx={{p: 2, pb: "max(16px, env(safe-area-inset-bottom))", flexShrink: 0, borderTop: "1px solid rgba(0, 0, 0, 0.12)"}}>
                    <Box sx={{mb: 2}}>
                        <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2}}>
                            <Typography variant="body1">{t("TotalPrice")}:</Typography>
                            <Typography variant="h6" sx={{textAlign: "right", minWidth: 0, overflowWrap: "anywhere"}}>{calculateGrandTotal() || "—"}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">{t("BasketCounts", {
                            lines: basketItems.reduce((count, distributor) => count + distributor.items.length, 0), units: calculateGrandItemCount()
                        })}</Typography>
                    </Box>

                    {/* Button group with Checkout, Clear All, and Cancel */}
                    <Stack direction="row" spacing={1} sx={{"& .MuiButton-root": {minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere"}}}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={basketItems.length === 0}
                            onClick={handleCheckout}
                        >
                            {t("Checkout")}
                        </Button>

                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            disabled={basketItems.length === 0}
                            onClick={() => setDialogOpen(true)}
                        >
                            {t("ClearAll")}
                        </Button>
                        <ConfirmationDialog
                            open={dialogOpen}
                            title={t("ClearAllBasket")}
                            message={t("ClearAllBasketMessage")}
                            onConfirm={handleConfirm}
                            onCancel={handleCancel}
                        />
                        <Button variant="outlined" color="inherit" fullWidth onClick={onClose}>
                            {t("Close")}
                        </Button>

                    </Stack>
                </Box>
                {selectedProduct && <ProductDetailsDialog open readOnly tenantType="PHARMACY" product={selectedProduct}
                    onClose={() => setSelectedProduct(null)}/>}
            </Box>
        </>
    )
}
export default BasketContainer
